import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Fingerprint, Plus, Trash2, X, Check, ShieldCheck, Receipt } from 'lucide-react'
import { api } from '../services/api'
import { isWebAuthnSupported, isBiometricAvailable, registerSigner, verifyBiometric } from '../services/webauthn'
import { formatCurrency, getCurrentMonth, getMonthLabel, getPreviousMonth, getNextMonth, formatDate } from '../utils/format'

const MONTH_SHORTS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']

// Mois pairs = Fév, Avr, Juin, Août, Oct, Déc (bimensuel confirmé)
function isEvenMonth(ym) {
  return parseInt(ym.split('-')[1]) % 2 === 0
}

export default function Cahier() {
  const [categories, setCategories] = useState([])
  const [allPayments, setAllPayments] = useState([])
  const [signers, setSigners] = useState([])
  const [month, setMonth] = useState(getCurrentMonth())
  const [year, setYear] = useState(new Date().getFullYear())
  const [viewMode, setViewMode] = useState('monthly') // 'monthly' | 'annual'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [webauthnSupported, setWebauthnSupported] = useState(false)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  const [webauthnChecked, setWebauthnChecked] = useState(false)

  // Modal double-signature (Loyer & Électricité/Eau)
  const [signModal, setSignModal] = useState(null) // { category, monthStr }
  const [signAmount, setSignAmount] = useState('')
  const [signSigner, setSignSigner] = useState('')
  const [verifying, setVerifying] = useState(false)

  // Modal paiement simple (WiFi / Gaz / IP TV)
  const [simpleModal, setSimpleModal] = useState(null) // { category, monthStr }
  const [simpleAmount, setSimpleAmount] = useState('')
  const [simpleNote, setSimpleNote] = useState('')
  const [simpleSaving, setSimpleSaving] = useState(false)

  // Modal enregistrement signataire
  const [showRegisterSigner, setShowRegisterSigner] = useState(false)
  const [signerName, setSignerName] = useState('')

  // Vérification WebAuthn au montage
  useEffect(() => {
    Promise.all([Promise.resolve(isWebAuthnSupported()), isBiometricAvailable()])
      .then(([sup, bio]) => { setWebauthnSupported(sup); setBiometricAvailable(bio); setWebauthnChecked(true) })
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [cats, hist, sigs] = await Promise.all([
        api.getCategories(),
        api.getPaymentHistory(),
        api.getSigners(),
      ])
      setCategories(cats.categories || [])
      setAllPayments(hist.payments || [])
      setSigners(sigs.signers || [])
    } catch (err) {
      setError(err.message || 'Erreur de chargement')
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Synchronisation temps réel : paiements et signataires
  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.some(ev => ['payment', 'signer'].includes(ev.entity))) {
        api.getPaymentHistory().then(h => setAllPayments(h.payments || [])).catch(() => {})
        api.getSigners().then(s => setSigners(s.signers || [])).catch(() => {})
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [])

  // ─── Résolution des 5 catégories du Cahier ──────────────────────────────────
  const flatCats = categories.flatMap(p => [p, ...(p.children || [])])
  const loyerCat  = flatCats.find(c => c.name === 'Loyer')
  const ewCat     = flatCats.find(c => c.name === 'Électricité / Eau')
  const wifiCat   = flatCats.find(c => c.name === 'WiFi')
  const gazCat    = flatCats.find(c => c.name === 'Gaz')
  const iptvCat   = flatCats.find(c => c.name === 'IP TV')

  // Tableau annuel : 12 mois de l'année sélectionnée
  const yearMonths = Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`)

  // ─── Helpers de statut ────────────────────────────────────────────────────

  // Double signature (Loyer / EW) : requiert 2 signataires distincts + 1 montant > 0
  function getDoubleSignInfo(catId, ym) {
    if (!catId) return { count: 0, hasPrimary: false, amount: 0, signerNames: [], paid: false, date: null }
    const records = allPayments.filter(p => p.category_id === catId && p.month === ym)
    const signerSet = new Set(records.map(r => r.paid_by).filter(Boolean))
    const primary = records.find(r => (r.amount || 0) > 0)
    const count = signerSet.size
    return {
      count,
      hasPrimary: !!primary,
      amount: primary?.amount || 0,
      signerNames: Array.from(signerSet),
      paid: count >= 2 && !!primary,
      date: primary?.paid_at || null,
    }
  }

  // Paiement simple (WiFi / Gaz) : 1 enregistrement suffit
  function getSimplePayInfo(catId, ym) {
    if (!catId) return { paid: false, amount: 0, note: '', date: null }
    const record = allPayments.find(p => p.category_id === catId && p.month === ym)
    return { paid: !!record, amount: record?.amount || 0, note: record?.note || '', date: record?.paid_at || null }
  }

  // IP TV annuel : 1 seul paiement dans l'année
  function getIptvStatus(ym) {
    if (!iptvCat) return { paid: false, record: null }
    const y = ym.split('-')[0]
    const record = allPayments.find(p => p.category_id === iptvCat.id && p.month.startsWith(y))
    return { paid: !!record, record }
  }

  // ─── Handlers : double signature ─────────────────────────────────────────

  function openSignModal(cat, ym) {
    const info = getDoubleSignInfo(cat.id, ym)
    setSignModal({ category: cat, monthStr: ym })
    setSignAmount(info.hasPrimary ? String(info.amount) : '')
    setSignSigner('')
    setError('')
  }

  const handleSign = async () => {
    if (!signModal || !signSigner) return
    setError('')
    setVerifying(true)
    try {
      const rpId = window.location.hostname
      const result = await verifyBiometric(rpId)
      const { category, monthStr } = signModal
      const info = getDoubleSignInfo(category.id, monthStr)
      const isFirst = !info.hasPrimary
      const amountVal = isFirst ? (parseFloat(signAmount) || 0) : 0
      if (isFirst && amountVal <= 0) throw new Error('Montant requis pour la 1ère signature')

      await api.recordPayment({
        category_id: category.id,
        category_name: category.name,
        month: monthStr,
        amount: amountVal,
        paid_by: signSigner,
        biometric_verified: 1,
        credential_id: result.credential_id,
        note: isFirst ? '' : `cosign:${category.id}:${monthStr}`,
      })

      setSignModal(null)
      const hist = await api.getPaymentHistory()
      setAllPayments(hist.payments || [])
    } catch (err) {
      setError(err.message)
    }
    setVerifying(false)
  }

  // ─── Handlers : paiement simple ──────────────────────────────────────────

  function openSimpleModal(cat, ym) {
    setSimpleModal({ category: cat, monthStr: ym })
    setSimpleAmount('')
    setSimpleNote('')
    setError('')
  }

  const handleSimplePay = async () => {
    if (!simpleModal) return
    setError('')
    setSimpleSaving(true)
    try {
      const { category, monthStr } = simpleModal
      await api.recordPayment({
        category_id: category.id,
        category_name: category.name,
        month: monthStr,
        amount: parseFloat(simpleAmount) || 0,
        paid_by: 'Confirmé',
        biometric_verified: 0,
        note: simpleNote.trim(),
      })
      setSimpleModal(null)
      const hist = await api.getPaymentHistory()
      setAllPayments(hist.payments || [])
    } catch (err) {
      setError(err.message)
    }
    setSimpleSaving(false)
  }

  // ─── Handlers : signataires ───────────────────────────────────────────────

  const handleRegisterSigner = async () => {
    if (!signerName.trim()) return
    setError('')
    try {
      const rpId = window.location.hostname
      await registerSigner(rpId, signerName.trim())
      setShowRegisterSigner(false)
      setSignerName('')
      fetchData()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDeleteSigner = async (id) => {
    if (!confirm('Supprimer ce signataire ?')) return
    try {
      await api.deleteSigner(id)
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  // ─── Helpers JSX ─────────────────────────────────────────────────────────

  // Ligne double-signature (Loyer / EW)
  function DoubleSignRow({ cat, ym }) {
    if (!cat) return null
    const info = getDoubleSignInfo(cat.id, ym)
    const sigBadge = info.paid
      ? <span className="badge bg-green-50 text-green-700 border border-green-200">✅ 2/2</span>
      : info.count === 1
        ? <span className="badge bg-amber-50 text-amber-700 border border-amber-200">⚠️ 1/2</span>
        : <span className="badge bg-slate-100 text-slate-500">0/2</span>
    return (
      <div className="flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-50">
        <span className="text-xl">{cat.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{cat.name}</p>
          {info.paid && <p className="text-xs text-slate-400 mt-0.5">{info.signerNames.join(' + ')}{info.amount > 0 ? ` • ${formatCurrency(info.amount)}` : ''}</p>}
          {!info.paid && info.count === 1 && <p className="text-xs text-amber-500 mt-0.5">Signé: {info.signerNames[0]} — en attente 2ᵉ signature</p>}
        </div>
        {sigBadge}
        {!info.paid && (
          <button onClick={() => openSignModal(cat, ym)} disabled={signers.length === 0 || !webauthnSupported} className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1 shrink-0 disabled:opacity-40">
            <Fingerprint size={14} /> Signer
          </button>
        )}
      </div>
    )
  }

  // Ligne paiement simple (WiFi / Gaz / IP TV)
  function SimpleRow({ cat, ym, recurrence }) {
    if (!cat) return null
    // IP TV : logique annuelle
    if (recurrence === 'annual') {
      const { paid, record } = getIptvStatus(ym)
      if (paid) {
        return (
          <div className="flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-50">
            <span className="text-xl">{cat.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{cat.name} <span className="text-xs text-slate-400">(annuel)</span></p>
              <p className="text-xs text-slate-400">{record?.month}{record?.amount > 0 ? ` • ${formatCurrency(record.amount)}` : ''}{record?.note ? ` • ${record.note}` : ''}</p>
            </div>
            <span className="badge bg-green-50 text-green-700 border border-green-200">✅ Payé</span>
          </div>
        )
      }
      return (
        <div className="flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-50">
          <span className="text-xl">{cat.icon}</span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{cat.name} <span className="text-xs text-slate-400">(annuel)</span></p>
            <p className="text-xs text-amber-500">Non payé cette année</p>
          </div>
          <button onClick={() => openSimpleModal(cat, ym)} className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1 shrink-0">
            <Check size={14} /> Marquer payé
          </button>
        </div>
      )
    }
    // Bimensuel : vérifier si dû ce mois
    const due = recurrence === 'bimonthly' ? isEvenMonth(ym) : true
    const info = getSimplePayInfo(cat.id, ym)
    return (
      <div className="flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-50">
        <span className="text-xl">{cat.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{cat.name}</p>
          {info.paid && <p className="text-xs text-slate-400 mt-0.5">{info.amount > 0 ? formatCurrency(info.amount) : ''}{info.note ? ` • ${info.note}` : ''}</p>}
          {!due && !info.paid && <p className="text-xs text-slate-400">Bimensuel — non dû ce mois</p>}
        </div>
        {info.paid
          ? <span className="badge bg-green-50 text-green-700 border border-green-200">✅ Payé</span>
          : due
            ? <button onClick={() => openSimpleModal(cat, ym)} className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1 shrink-0"><Check size={14} /> Marquer payé</button>
            : <span className="badge bg-slate-100 text-slate-400">Non dû</span>
        }
      </div>
    )
  }

  return (
    <div className="space-y-4">

      {/* ─── Sélecteur de vue + navigation ─── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-slate-100 rounded-xl overflow-hidden text-sm font-medium flex-1 min-w-0">
          <button onClick={() => setViewMode('monthly')} className={`flex-1 px-3 py-1.5 transition-colors ${viewMode === 'monthly' ? 'bg-primary-700 text-white' : 'text-slate-500'}`}>Mensuel</button>
          <button onClick={() => setViewMode('annual')} className={`flex-1 px-3 py-1.5 transition-colors ${viewMode === 'annual' ? 'bg-primary-700 text-white' : 'text-slate-500'}`}>Annuel</button>
        </div>
        {viewMode === 'monthly' && (
          <>
            <button onClick={() => setMonth(getPreviousMonth(month))} className="p-2 rounded-lg active:bg-slate-100"><ChevronLeft size={20} /></button>
            <span className="text-sm font-semibold capitalize whitespace-nowrap">{getMonthLabel(month)}</span>
            <button onClick={() => setMonth(getNextMonth(month))} className="p-2 rounded-lg active:bg-slate-100"><ChevronRight size={20} /></button>
          </>
        )}
        {viewMode === 'annual' && (
          <>
            <button onClick={() => setYear(y => y - 1)} className="p-2 rounded-lg active:bg-slate-100"><ChevronLeft size={20} /></button>
            <span className="text-sm font-semibold">{year}</span>
            <button onClick={() => setYear(y => y + 1)} className="p-2 rounded-lg active:bg-slate-100"><ChevronRight size={20} /></button>
          </>
        )}
      </div>

      {/* ─── Biométrie + signataires ─── */}
      <div className={`card flex items-center gap-3 ${webauthnSupported ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <Fingerprint size={20} className={webauthnSupported ? 'text-green-600' : 'text-amber-600'} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {!webauthnChecked ? 'Vérification…' : webauthnSupported ? (biometricAvailable ? 'Biométrie disponible' : 'WebAuthn supporté') : 'WebAuthn non supporté sur cet appareil'}
          </p>
          <p className="text-xs text-slate-500">{signers.length} signataire(s) enregistré(s)</p>
        </div>
        <button onClick={() => setShowRegisterSigner(true)} className="btn-primary text-xs px-3 py-2 flex items-center gap-1 shrink-0">
          <Plus size={14} /> Signataire
        </button>
      </div>

      {signers.length > 0 && (
        <div className="card space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Signataires enregistrés</p>
          {signers.map(s => (
            <div key={s.id} className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-green-600 shrink-0" />
              <span className="flex-1 text-sm font-medium">{s.signer_name}</span>
              <span className="text-xs text-slate-400">{formatDate(s.created_at)}</span>
              {s.can_delete && (
                <button onClick={() => handleDeleteSigner(s.id)} className="p-1 text-slate-300 active:text-red-500"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-500 text-center bg-red-50 rounded-xl p-3">{error}</p>}

      {/* ─── VUE MENSUELLE ─── */}
      {viewMode === 'monthly' && (
        <div className="space-y-4">

          {/* Section 1 : Loyer & Charges locatives (double signature biométrique) */}
          <div className="rounded-2xl overflow-hidden border border-red-100 bg-white shadow-sm">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border-b border-red-100">
              <span className="text-lg">🏠</span>
              <p className="font-semibold text-sm text-red-700 flex-1">Loyer & Charges locatives</p>
              <span className="text-xs text-red-400 flex items-center gap-1"><Fingerprint size={12} /> Double signature</span>
            </div>
            <DoubleSignRow cat={loyerCat} ym={month} />
            {isEvenMonth(month)
              ? <DoubleSignRow cat={ewCat} ym={month} />
              : ewCat && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="text-xl">{ewCat.icon}</span>
                  <div className="flex-1"><p className="font-medium text-sm">{ewCat.name}</p><p className="text-xs text-slate-400">Bimensuel (mois pairs) — non dû ce mois</p></div>
                  <span className="badge bg-slate-100 text-slate-400">Non dû</span>
                </div>
              )
            }
            {!loyerCat && <p className="px-4 py-3 text-sm text-amber-500">⚠️ Catégorie "Loyer" introuvable — vérifiez la migration DB</p>}
            {!ewCat && <p className="px-4 py-2 text-sm text-amber-500">⚠️ Catégorie "Électricité / Eau" introuvable</p>}
          </div>

          {/* Section 2 : Factures courantes (paiement simple en agence) */}
          <div className="rounded-2xl overflow-hidden border border-blue-100 bg-white shadow-sm">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border-b border-blue-100">
              <span className="text-lg">📡</span>
              <p className="font-semibold text-sm text-blue-700 flex-1">Factures courantes</p>
              <span className="text-xs text-blue-400 flex items-center gap-1"><Receipt size={12} /> Reçu agence</span>
            </div>
            <SimpleRow cat={wifiCat} ym={month} recurrence="monthly" />
            <SimpleRow cat={gazCat} ym={month} recurrence="bimonthly" />
            <SimpleRow cat={iptvCat} ym={month} recurrence="annual" />
            {!wifiCat && <p className="px-4 py-2 text-sm text-amber-500">⚠️ Catégorie "WiFi" introuvable</p>}
            {!gazCat && <p className="px-4 py-2 text-sm text-amber-500">⚠️ Catégorie "Gaz" introuvable</p>}
            {!iptvCat && <p className="px-4 py-2 text-sm text-amber-500">⚠️ Catégorie "IP TV" introuvable</p>}
          </div>

        </div>
      )}

      {/* ─── VUE ANNUELLE ─── */}
      {viewMode === 'annual' && (
        <div className="space-y-4">

          {/* Tableau 1 : double signature (Loyer + EW) */}
          <div className="rounded-2xl overflow-hidden border border-slate-100 bg-white shadow-sm">
            <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-xs font-medium text-red-700 flex items-center gap-1">
              <Fingerprint size={12} /> Loyer & Électricité/Eau — Double signature biométrique
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[500px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-3 py-2 text-left text-slate-500 bg-slate-50 min-w-[90px] font-medium">Charge</th>
                    {MONTH_SHORTS.map((m, i) => (
                      <th key={i} className="px-1 py-2 text-center text-slate-500 bg-slate-50 font-medium">{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[loyerCat, ewCat].filter(Boolean).map(cat => (
                    <tr key={cat.id} className="border-b border-slate-50 last:border-0">
                      <td className="px-3 py-2 font-medium text-slate-700">{cat.icon} {cat.name}</td>
                      {yearMonths.map(ym => {
                        const isEW = cat === ewCat
                        const due = !isEW || isEvenMonth(ym)
                        if (!due) return <td key={ym} className="px-1 py-2 text-center text-slate-300">—</td>
                        const info = getDoubleSignInfo(cat.id, ym)
                        const bg = info.paid ? 'bg-green-50 text-green-700' : info.count === 1 ? 'bg-amber-50 text-amber-700' : 'bg-white text-slate-400'
                        const lbl = info.paid ? '2/2' : info.count === 1 ? '1/2' : '0/2'
                        return (
                          <td key={ym} className="px-1 py-2">
                            <button
                              onClick={() => !info.paid && openSignModal(cat, ym)}
                              disabled={info.paid || signers.length === 0 || !webauthnSupported}
                              className={`w-full rounded py-1 text-center font-medium disabled:cursor-default transition-colors ${bg}`}
                            >{lbl}</button>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tableau 2 : paiement simple (WiFi + Gaz + IP TV) */}
          <div className="rounded-2xl overflow-hidden border border-slate-100 bg-white shadow-sm">
            <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 text-xs font-medium text-blue-700 flex items-center gap-1">
              <Receipt size={12} /> Factures courantes — Reçu agence
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[500px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-3 py-2 text-left text-slate-500 bg-slate-50 min-w-[90px] font-medium">Charge</th>
                    {MONTH_SHORTS.map((m, i) => (
                      <th key={i} className="px-1 py-2 text-center text-slate-500 bg-slate-50 font-medium">{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[wifiCat, gazCat].filter(Boolean).map(cat => {
                    const isGaz = cat === gazCat
                    return (
                      <tr key={cat.id} className="border-b border-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-700">{cat.icon} {cat.name}</td>
                        {yearMonths.map(ym => {
                          if (isGaz && !isEvenMonth(ym)) return <td key={ym} className="px-1 py-2 text-center text-slate-300">—</td>
                          const info = getSimplePayInfo(cat.id, ym)
                          return (
                            <td key={ym} className="px-1 py-2">
                              <button
                                onClick={() => !info.paid && openSimpleModal(cat, ym)}
                                className={`w-full rounded py-1 text-center font-medium transition-colors ${info.paid ? 'bg-green-50 text-green-700' : 'bg-white text-slate-400 active:bg-slate-50'}`}
                              >{info.paid ? '✓' : '○'}</button>
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                  {iptvCat && (() => {
                    const { paid, record } = getIptvStatus(`${year}-01`)
                    return (
                      <tr>
                        <td className="px-3 py-2 font-medium text-slate-700">{iptvCat.icon} {iptvCat.name} <span className="text-slate-400">(ann.)</span></td>
                        {yearMonths.map(ym => {
                          const thisMonthPaid = allPayments.find(p => p.category_id === iptvCat.id && p.month === ym)
                          return (
                            <td key={ym} className="px-1 py-2">
                              {thisMonthPaid
                                ? <span className="flex justify-center text-green-600">✓</span>
                                : paid
                                  ? <span className="flex justify-center text-slate-300">—</span>
                                  : <button onClick={() => openSimpleModal(iptvCat, ym)} className="w-full rounded py-1 text-center text-slate-400 active:bg-slate-50">○</button>
                              }
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })()}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* ─── Modal : Enregistrer signataire ─── */}
      {showRegisterSigner && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowRegisterSigner(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Fingerprint size={20} /> Nouveau signataire</h3>
              <button onClick={() => setShowRegisterSigner(false)} className="p-1"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-3">Enregistrez une personne (femme, logeuse) qui signera les paiements avec sa biométrie.</p>
            {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
            <input type="text" value={signerName} onChange={e => setSignerName(e.target.value)} className="input mb-3" placeholder="Ex: Ma femme, Logeuse..." autoFocus />
            <button onClick={handleRegisterSigner} disabled={!signerName.trim()} className="btn-primary w-full flex items-center justify-center gap-2">
              <Fingerprint size={18} /> Enregistrer la biométrie
            </button>
          </div>
        </div>
      )}

      {/* ─── Modal : Signature biométrique (Loyer / EW) ─── */}
      {signModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setSignModal(null)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{signModal.category.icon} {signModal.category.name}</h3>
              <button onClick={() => setSignModal(null)} className="p-1"><X size={20} /></button>
            </div>
            {(() => {
              const info = getDoubleSignInfo(signModal.category.id, signModal.monthStr)
              const isFirst = !info.hasPrimary
              return (
                <div className="space-y-3">
                  <div className="card bg-slate-50">
                    <p className="text-sm text-slate-500 capitalize">{getMonthLabel(signModal.monthStr)}</p>
                    <p className="text-xs text-slate-400 mt-1">Signatures : {info.count}/2 {info.signerNames.length > 0 ? `— déjà signé par ${info.signerNames.join(', ')}` : ''}</p>
                  </div>
                  {isFirst && (
                    <div>
                      <label className="label">Montant payé (FCFA)</label>
                      <input type="number" className="input" value={signAmount} onChange={e => setSignAmount(e.target.value)} placeholder="0" />
                    </div>
                  )}
                  <div>
                    <label className="label">Qui signe maintenant ?</label>
                    <select value={signSigner} onChange={e => setSignSigner(e.target.value)} className="input">
                      <option value="">Sélectionner...</option>
                      {signers.map(s => <option key={s.id} value={s.signer_name}>{s.signer_name}</option>)}
                    </select>
                  </div>
                  {error && <p className="text-sm text-red-500">{error}</p>}
                  <button onClick={handleSign} disabled={!signSigner || verifying} className="btn-primary w-full flex items-center justify-center gap-2">
                    {verifying
                      ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Vérification...</>
                      : <><Fingerprint size={18} /> Authentifier avec biométrie</>
                    }
                  </button>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* ─── Modal : Paiement simple (WiFi / Gaz / IP TV) ─── */}
      {simpleModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setSimpleModal(null)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Receipt size={20} /> {simpleModal.category.icon} {simpleModal.category.name}</h3>
              <button onClick={() => setSimpleModal(null)} className="p-1"><X size={20} /></button>
            </div>
            <div className="card bg-slate-50 mb-4">
              <p className="text-sm text-slate-500 capitalize">{getMonthLabel(simpleModal.monthStr)}</p>
              <p className="text-xs text-slate-400 mt-1">Paiement effectué en agence — aucune signature requise</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label">Montant payé (optionnel)</label>
                <input type="number" className="input" value={simpleAmount} onChange={e => setSimpleAmount(e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className="label">N° de reçu / Note (optionnel)</label>
                <input type="text" className="input" value={simpleNote} onChange={e => setSimpleNote(e.target.value)} placeholder="Ex: Reçu 12345, Agence MTN..." />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <button onClick={handleSimplePay} disabled={simpleSaving} className="btn-primary w-full flex items-center justify-center gap-2">
                {simpleSaving
                  ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Enregistrement...</>
                  : <><Check size={18} /> Confirmer le paiement</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
